import { Box, Flex, Text } from '@chakra-ui/react'
import type { OrganizationUnit } from 'BrokerTsLib/src/dtos'
import {
  getOrganizationUnitTypeName,
  OrganizationUnitType,
} from 'brokertslib/src/enums'
import { useGetAcquisitionNumbers } from 'brokertslib/src/hooks/useOrganizationUnit'
import { formatDateTime } from 'BrokerTsLib/src/Utils/formatPrice'
import { useMemo } from 'react'

import { Card, CardBody } from '../../components/Card'
import { useAuth } from '../../context/AuthContext'
import { Building, TextUnorderedList } from '../../icons'
import { AcquisitionNumbersTable } from '../shared/AcquisitionNumbersTable'
import { Address } from '../shared/Address'
import { BusinessId } from '../shared/BusinessId'
import { Contact } from '../shared/Contact'
import { SectionHeading } from '../shared/SectionHeading'
import { UserLink } from '../shared/UserLink'
import { DependentOrganizationUnitList } from './DependentOrganizationUnitList'
import { OrganizationDiscounts } from './OrganizationDiscounts'
import { OrganizationLogoUploadForm } from './OrganizationLogoUploadForm'
import { OrganizationUserList } from './OrganizationUserList'

export const OrganizationDetail = ({
  organization,
}: {
  organization: OrganizationUnit
}) => {
  const {
    addressPostCode,
    addressStreet,
    addressStreetNumber,
    addressCity,
    ico,
    contactName,
    phone,
    mobilePhone,
    email,
    modifiedBy,
    modifiedById,
    dateModified,
    dateCreated,
    createdBy,
    createdById,
  } = organization
  const auth = useAuth()

  const { data: acquisitionNumbersData } = useGetAcquisitionNumbers(
    organization?.id,
    undefined,
    {
      enabled: !!organization,
    },
  )
  const acquisitionNumbers = useMemo(
    () => acquisitionNumbersData?.data ?? [],
    [acquisitionNumbersData],
  )

  return (
    <>
      {organization?.description && (
        <Flex flexDir={'column'} gap={2}>
          {organization?.description && (
            <Text size={'md'} maxWidth={'xl'}>
              <TextUnorderedList boxSize={'16px'} mr={4} mt={-0.5} />
              {organization?.description}
            </Text>
          )}
        </Flex>
      )}

      <Card>
        <CardBody sx={{ p: 4, pl: 4 }}>
          <Box
            sx={{
              display: 'grid',
              gap: 8,
              gridTemplateColumns: { base: '1fr', md: '1fr 1fr' },
            }}
          >
            <Box sx={{ display: 'grid', gap: 8 }}>
              <Box pl={2}>
                <SectionHeading>Společnost</SectionHeading>
                <Address
                  postCode={addressPostCode}
                  street={addressStreet}
                  streetNumber={addressStreetNumber}
                  city={addressCity}
                />

                {ico && <BusinessId businessId={ico} />}

                <Flex my={1} sx={{ gap: 2, flexWrap: 'wrap' }}>
                  <Building color="secondary.500" />
                  {`Typ: ${getOrganizationUnitTypeName(organization?.type)}`}
                </Flex>

                <SectionHeading sx={{ mt: 6 }}>Kontaktní osoba</SectionHeading>
                {contactName || phone || mobilePhone || email ? (
                  <Contact
                    name={contactName}
                    phone={phone}
                    email={email}
                    mobilePhone={mobilePhone}
                  />
                ) : (
                  <Text sx={{ color: 'gray.500' }}>
                    Kontaktní informace nejsou k dispozici
                  </Text>
                )}
                <OrganizationLogoUploadForm
                  organizationUnitId={organization.id}
                  logoUrl={organization.logoPartner}
                />
              </Box>

              {dateModified && (
                <Text
                  sx={{
                    color: 'gray.500',
                    fontSize: 'sm',
                    alignContent: 'end',
                  }}
                >
                  Poslední změna: {formatDateTime(dateModified)}{' '}
                  <UserLink name={modifiedBy} userId={modifiedById} />
                </Text>
              )}
              {dateCreated && !dateModified && (
                <Text
                  sx={{
                    color: 'gray.500',
                    fontSize: 'sm',
                    alignContent: 'end',
                  }}
                >
                  Vytvořeno: {formatDateTime(dateCreated)}{' '}
                  <UserLink name={createdBy} userId={createdById} />
                </Text>
              )}
            </Box>
            <Box sx={{ display: 'grid', alignContent: 'start' }}>
              <SectionHeading sx={{ pl: 4 }}>Ziskatelská čísla</SectionHeading>

              <AcquisitionNumbersTable rows={acquisitionNumbers} />
            </Box>
          </Box>
        </CardBody>
      </Card>

      {auth?.user?.allowDisplayOrganizationStructure && (
        <>
          <OrganizationUserList
            organizationUnitId={organization.id}
            effectivelyValid={organization.effectivelyValid}
          />

          {[
            OrganizationUnitType.Partner,
            OrganizationUnitType.BranchOffice,
            OrganizationUnitType.Manager,
          ].includes(organization.type) && (
            <DependentOrganizationUnitList
              organizationUnitId={organization.id}
              effectivalyValid={organization.effectivelyValid}
            />
          )}

          <OrganizationDiscounts organizationUnitId={organization.id} />
        </>
      )}
    </>
  )
}
